/**
 * External dependencies
 */
import React from 'react';
import { Box, Stack, Text } from '@towersystems/towersystems-design-system';
import { map, size } from 'lodash';

/**
 * Internal dependencies
 */
import Button from 'components/button';
import AnnotatedSection from 'components/annotated-section';
import Icon from 'components/icon';
import Card from 'components/card';
import ConnectionLocationApiButton from './connection-location-api-button';

/**
 * Style dependencies
 */
import './styles.scss';

const DefaultRender = ({
	disabled,
	locationWebsites,
	connectionType,
	connectionId,
	locationGroups,
	locations,
	isShown,
	...props
}) => {
	return (
		<Stack space='medium'>
			<Stack space='xlarge' dividers>
				<AnnotatedSection>
					<AnnotatedSection.Annotation
						title='Locations'
						description={
							<Text>
								View your current Retailer locations. {<a href='/settings/locations'>Edit location</a>}
							</Text>
						}
					/>
					<AnnotatedSection.Content>
						<Card>
							{map(locations, (location) => {
								return (
									<Card.Section key={location.id}>
										<Box display='flex' alignItems='center'>
											<Icon size='30' name='VscLocation' type='vsc' marginRight={25} />
											<Box>
												<Text weight='strong'>{location.name}</Text>
												<Text size='small'>Location key {location.number}</Text>
											</Box>
										</Box>
									</Card.Section>
								);
							})}
						</Card>
					</AnnotatedSection.Content>
				</AnnotatedSection>

				<AnnotatedSection>
					<AnnotatedSection.Annotation
						title='Inventory Groups'
						description='Create Inventory group to send inventory data to your eCommerce platform.'
					/>
					<AnnotatedSection.Content>
						<Card>
							<Card.Header heading='Current location groups'>
								<p>
									<Text>At least one location group is required to sync inventory data.</Text>
								</p>
							</Card.Header>
							{map(locationGroups, (locationGroup) => {
								return (
									<Card.Section key={locationGroup.id}>
										<Box display='flex' alignItems='center' justifyContent='spaceBetween'>
											<Box>
												<Text>{locationGroup.name}</Text>
												<Text size='xsmall'>{size(locationGroup.locations)} location(s)</Text>
											</Box>
											<Box>
												<Button
													onClick={() => {
														props.startEditingLocationGroup(locationGroup.id);
													}}
												>
													Edit
												</Button>
											</Box>
										</Box>
									</Card.Section>
								);
							})}
							<Card.Section display={isShown}>
								<Box display='flex' alignItems='center' justifyContent='spaceBetween'>
									<Text className='card__section--text-capitalize'>
										Import {' '}
										{connectionType
											.split('_')
											.join(' ')
											.charAt(0) +
										connectionType
											.split('_')
											.join(' ')
											.slice(1)}{' '} location
									</Text>
									<ConnectionLocationApiButton connectionId={connectionId} disabled={disabled} />
								</Box>
							</Card.Section>
							<Card.Footer>
								<Button
									onClick={() => props.startEditingLocationGroup(null, { connection: connectionId })}
									primary
									disabled={disabled}
								>
									Add location group
								</Button>
							</Card.Footer>
						</Card>
					</AnnotatedSection.Content>
				</AnnotatedSection>

				{connectionType === 'magento' ? (
					<AnnotatedSection>
						<AnnotatedSection.Annotation
							title='Website Locations'
							description='For multisite ecommerce platform, map a Retailer location with a website from your ecommerce platform.'
						/>
						<AnnotatedSection.Content>
							<Card>
								<Card.Header heading='Current location and website mapping'>
									<p>
										<Text>
											For ecommerce platform in multisite structure and Retailer location mapping
											only.
										</Text>
									</p>
								</Card.Header>
								{map(locationWebsites, (locationWebsite) => {
									return (
										<Card.Section key={locationWebsite.id}>
											<Box display='flex' alignItems='center' justifyContent='spaceBetween'>
												<Box>
													<Text>{locationWebsite.name}</Text>
													<Text size='xsmall'>
														{size(locationWebsite.locations)} location(s)
													</Text>
												</Box>
												<Box>
													<Button
														onClick={() => {
															props.startEditingLocationWebsite(locationWebsite.id);
														}}
													>
														Edit
													</Button>
												</Box>
											</Box>
										</Card.Section>
									);
								})}

								<Card.Footer>
									<Button
										onClick={() =>
											props.startEditingLocationWebsite(null, { connection: connectionId })
										}
										primary
										disabled={disabled}
									>
										Add website location
									</Button>
								</Card.Footer>
							</Card>
						</AnnotatedSection.Content>
					</AnnotatedSection>
				) : (
					''
				)}
			</Stack>
		</Stack>
	);
};

export default DefaultRender;
